import { useState, useEffect } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';


const Expertise = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);

  useEffect(() => {
    fetchServices();
    fetchFormTemplates();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get(`/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services', error);
    }
  };

  const fetchFormTemplates = async () => {
    try {
      const response = await api.get(`/form-templates`);
      setFormTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch form templates', error);
    }
  };

  const getFormFields = (formId) => {
    const template = formTemplates.find(t => t.id === formId);
    return template?.fields || [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post(`/form-submissions`, {
        form_template_id: selectedService.linked_form_id,
        form_name: selectedService.title,
        data: formData
      });

      toast.success('Form submitted successfully! We will contact you shortly.');
      setSelectedService(null);
      setFormData({});
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>

      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-primary text-center mb-8" data-testid="expertise-heading">
            Our Expertise
          </h1>
          <p className="font-sans text-lg md:text-xl text-center text-muted-foreground mb-16 max-w-4xl mx-auto leading-relaxed">
            Welcome to Forty Chambers, your premier destination for top-tier legal advisory services in Ontario.
            We prioritise clear communication, transparency, and affordable fees.
          </p>

          {services.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className="bg-white border border-border/40 overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group"
                  data-testid={`service-card-${index}`}
                >
                  <div
                    className="h-48 bg-cover bg-center"
                    style={{ backgroundImage: `url(${service.image_data || service.image_url})` }}
                  />
                  <div className="p-6">
                    <h3 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-4">
                      {service.title}
                    </h3>
                    <p className="font-sans text-base text-muted-foreground leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <Button
                      onClick={() => setSelectedService(service)}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg font-medium"
                      data-testid={`service-button-${service.id}`}
                    >
                      Request Consultation
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="font-sans text-lg text-muted-foreground">
                No services available at the moment. Please check back later.
              </p>
            </div>
          )}

          <div className="mt-16 text-center bg-secondary p-8 border border-border">
            <h2 className="font-serif text-3xl font-semibold text-primary mb-4">Ready to Get Started?</h2>
            <p className="font-sans text-lg text-muted-foreground mb-6 max-w-3xl mx-auto">
              Contact us today to discuss your legal needs and secure the best possible outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Form Dialog */}
      <Dialog open={!!selectedService} onOpenChange={() => { setSelectedService(null); setFormData({}); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {selectedService?.title} - Intake Form
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {getFormFields(selectedService?.linked_form_id).map((field) => (
              <div key={field.name}>
                <Label htmlFor={field.name} className="text-sm font-medium mb-2 block">
                  {field.label} {field.required && <span className="text-destructive">*</span>}
                </Label>
                {field.type === 'textarea' ? (
                  <Textarea
                    id={field.name}
                    required={field.required}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="w-full"
                    rows={4}
                    data-testid={`field-${field.name}`}
                  />
                ) : field.type === 'select' ? (
                  <Select
                    value={formData[field.name] || ''}
                    onValueChange={(value) => setFormData({ ...formData, [field.name]: value })}
                    required={field.required}
                  >
                    <SelectTrigger className="w-full" data-testid={`field-${field.name}`}>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options?.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={field.name}
                    type={field.type}
                    required={field.required}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="w-full"
                    data-testid={`field-${field.name}`}
                  />
                )}
              </div>
            ))}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setSelectedService(null); setFormData({}); }}
                className="flex-1"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-button"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </PageLayout>
  );
};

export default Expertise;
